// NOTE: this is designed to be compatible with @reduxjs/toolkit/query/react

export type Resource<T> = Uninitialized<T> | Loading<T> | Success<T> | Failure<T>;
export type ResourceObject<T> = DistributivePick<Resource<T>, ResourcePropKey>;
export type ResourceFailure<T> = Pick<ResourceObject<T>, ResourceDataKey>;

type ResourceDataKey = 'data' | 'error';
type ResourceFlagKey = 'isLoading' | 'isFetching' | 'isSuccess' | 'isError' | 'isUninitialized';

type ResourcePropKey = ResourceDataKey | ResourceFlagKey;
type DistributivePick<T, K extends keyof T> = T extends unknown ? {[P in K]: T[P]} : never;

export namespace Resource {
    export type InferData<R extends Resource<unknown>> = R extends Resource<infer TData> ? TData : never;

    export type Or<T> = Resource<T> | T;

    export function isResource(thing: unknown): thing is Resource<unknown> {
        return thing instanceof ResourcePrototype;
    }

    export function lift<T>(thing: T | Resource<T>): Resource<T> {
        if (Resource.isResource(thing)) {
            return thing;
        } else {
            return Success.from(thing);
        }
    }

    type Overload<A, T> = A & Partial<Omit<ResourceObject<T> & A, keyof A>>;

    type Overload_U<T> = Overload<{isUninitialized: true}, T>;
    type Overload_L<T> = Overload<{isLoading: true}, T>;
    type Overload_F<T> = Overload<{isError: true}, T>;
    type Overload_S<T> = Overload<{isSuccess: true; data: T}, T>;
    type SomeOverload<T> = Overload_U<T> | Overload_L<T> | Overload_F<T> | Overload_S<T>;

    export function from<T>(resourceLike: Overload_U<T>): Uninitialized<T>;
    export function from<T>(resourceLike: Overload_L<T>): Loading<T>;
    export function from<T>(resourceLike: Overload_F<T>): Failure<T>;
    export function from<T>(resourceLike: Overload_S<T>): Success<T>;
    export function from<T>(resourceLike: SomeOverload<T>): Resource<T>;
    export function from<T>(resourceLike: SomeOverload<T>): Resource<T> {
        if (resourceLike.isError || (resourceLike.isError == null && resourceLike.error)) {
            return Failure.from(resourceLike.error, resourceLike.data);
        }
        if (resourceLike.isSuccess || (resourceLike.isSuccess == null && resourceLike.data)) {
            return Success.from(resourceLike.data!, resourceLike.isFetching);
        }
        if (resourceLike.isLoading) {
            return Loading.instance<T>();
        } else {
            return Uninitialized.instance<T>();
        }
    }

    export function all<TResourceTuple extends readonly Resource<unknown>[]>(...resources: TResourceTuple) {
        type DataArray = {[K in keyof TResourceTuple]: Data<TResourceTuple[K]>};

        for (const resource of resources) {
            if (resource.isUninitialized) {
                return Uninitialized.instance<DataArray>();
            }
            if (resource.isError) {
                return Failure.from<DataArray>(resource.error);
            }
            if (resource.isLoading) {
                return Loading.instance<DataArray>();
            }
        }

        return Resource.lift(resources.map(resource => resource.data) as DataArray);
    }

    export const mapSuccess =
        <T>(instance: Pick<Success<T>, ResourcePropKey>) =>
        <To>(mapper: Mapper<T, To>): Resource<To> =>
            lift(mapper(instance.data));

    // Mappers behavior is unstable, might stabilize later but should not be used right now
    // export const mapUninitialized =
    //     <T>(_instance: Pick<Uninitialized<T>, ResourcePropKey>) =>
    //     <To>(mapper: Mapper<undefined, To>): Resource<To> =>
    //         lift(mapper(undefined));

    // export const mapLoading =
    //     <T>(_instance: Pick<Loading<T>, ResourcePropKey>) =>
    //     <To>(mapper: Mapper<undefined, To>): Resource<To> =>
    //         lift(mapper(undefined));

    // export const mapFailure =
    //     <T>(instance: Pick<Failure<T>, ResourcePropKey>) =>
    //     <To>(mapper: Mapper<ResourceFailure<T>, To>): Resource<To> =>
    //         lift(mapper({data: instance.data, error: instance.error}));

    export type Data<T extends Resource<unknown>> = T extends Resource<infer U> ? U : never;

    export type UninitializedType<T> = Uninitialized<T>;
    export type LoadingType<T> = Loading<T>;
    export type SuccessType<T> = Success<T>;
    export type FailureType<T> = Failure<T>;
}

type Mapper<From, To> = (currentData: From) => To | Resource<To>;
type MapFn<From> = <To>(mapper: Mapper<From, To>) => Resource<To>;

abstract class ResourcePrototype<T> {
    readonly mapSuccess: MapFn<T> = () =>
        // @ts-ignore
        this;

    // readonly mapUninitialized: MapFn<undefined> = () =>
    //     // @ts-ignore
    //     this;

    // readonly mapLoading: MapFn<undefined> = () =>
    //     // @ts-ignore
    //     this;

    // readonly mapFailure: MapFn<ResourceFailure<T>> = () =>
    //     // @ts-ignore
    //     this;
}

// prettier-ignore
class Uninitialized<T> extends ResourcePrototype<T> {
    readonly error?: undefined;
    readonly data?: undefined;

    get isFetching() { return false as const; }
    get isLoading() { return false as const; }
    get isSuccess() { return false as const; }
    get isError() { return false as const; }
    get isUninitialized() { return true as const; }

    private static __instance = new Uninitialized();
    static instance<T>() {
        return Uninitialized.__instance as Uninitialized<T>;
    }

    private constructor() {
        super();
    }
}

// prettier-ignore
class Loading<T> extends ResourcePrototype<T> {
    readonly error?: unknown;
    /** Never not undefined in loading state, only like this for compatibility of types  */
    readonly data?: T | undefined;

    /**
     * For compatibility with RTK Query this is boolean, event thou it makes no sense for resource to be loading but not fetching.
     * Proof that it always same:
     * https://github.com/reduxjs/redux-toolkit/blob/4afef4af288d1352975175de2f6dce98ce3759e4/packages/toolkit/src/query/react/buildHooks.ts#L633-L634
     */
    get isFetching(): boolean { return true }
    get isLoading() { return true as const; }
    get isSuccess() { return false as const; }
    get isError() { return false as const; }
    get isUninitialized() { return false as const; }

    private static __instance = new Loading();
    static instance<T>() {
        return Loading.__instance as Loading<T>;
    }

    private constructor(data?: T) {
        super();
        this.data = data;
    }
}

// prettier-ignore
class Failure<T> extends ResourcePrototype<T> {
    readonly error!: unknown;
    readonly data?: T | undefined;

    get isFetching() { return false as const; }
    get isLoading() { return false as const; }
    get isSuccess() { return false as const; }
    get isError() { return true as const; }
    get isUninitialized() { return false as const; }

    static from<T>(error: unknown, data?: T | undefined): Failure<T> {
        return new Failure<T>(error, data);
    }

    private constructor(error: unknown, data: T | undefined) {
        super();
        this.error = error;
        this.data = data;
    }
}

// prettier-ignore
class Success<T> extends ResourcePrototype<T> {
    readonly error: undefined;
    readonly data: T;

    readonly isFetching: boolean
    get isLoading() { return false as const; }
    get isSuccess() { return true as const; }
    get isError() { return false as const; }
    get isUninitialized() { return false as const; }

    readonly mapSuccess = Resource.mapSuccess(this);

    static from<T>(data: T, isFetching = false): Success<T> {
        return new Success<T>(data, isFetching);
    }

    private constructor(data: T, isFetching: boolean) {
        super();
        this.data = data;
        this.isFetching = isFetching;
    }
}
